import React from 'react';
import { Flame, Leaf, Zap } from 'lucide-react';
import { PRIMAL_STRIKE_FEATURE_ID, hasPotentSpellcasting, hasPrimalStrike, primalStrikeDice } from '../../../shared/rules/druid.mjs';
import { isIncapacitated } from '../../../shared/rules/barbarianRage.mjs';
import { getLand, getLandSpells, natureWardResistance } from '../../../shared/rules/subclasses/druid/land.mjs';
import { notify } from '../../../utils/notification';

/**
 * Reminder tiles for the Druid features that work on their own (2024), like the Cleric's passive tiles. Nothing is spent
 * here; Primal Strike asks when you hit, and dims once it has been used this turn. A Circle of the Land Druid sees the
 * land chosen at the last Long Rest, and the Resistance Nature's Ward gives from it.
 */
const words = ids => ids.map(id => id.split('-').map(part => (part === 'of' ? part : part[0].toUpperCase() + part.slice(1))).join(' ')).join(', ');
const capital = text => text[0].toUpperCase() + text.slice(1);
export const druidPassiveTiles = (form, characterId, combatState) => {
  const id = String(characterId);
  const incapacitated = isIncapacitated({ ...(form || {}), combatantId: id }, combatState || {});
  const claim = combatState?.turnFeatureUsage?.[id]?.[PRIMAL_STRIKE_FEATURE_ID];
  const used = Boolean(claim) && claim.turnSequence === combatState?.turnSequence;
  const strike = hasPrimalStrike(form) ? primalStrikeDice(form) : 0;
  const land = getLand(form);
  const ward = natureWardResistance(form);
  return [
    land && { id: 'land', name: `${land.name} land`, Icon: Leaf, accent: '#9fe0a0', lit: true, value: ward ? capital(ward) : 'Land',
      about: `Your Circle Spells come from the ${land.name} land: ${words(getLandSpells(form))}.`
        + (ward ? ` Nature’s Ward: you have Resistance to ${capital(ward)} damage, and you have Immunity to the Poisoned condition.` : '')
        + ' You can choose another land when you finish a Long Rest.' },
    strike && { id: 'primal-strike', name: 'Primal Strike', Icon: Zap, accent: '#9fd3ff', lit: !used && !incapacitated, value: used ? 'Used' : `${strike}d8`,
      about: `Once on each of your turns when you hit a creature with a weapon, you can deal an extra ${strike}d8 Cold, Fire, Lightning or Thunder damage.` },
    hasPotentSpellcasting(form) && { id: 'potent-spellcasting', name: 'Potent Spellcasting', Icon: Flame, accent: '#ffb86b', lit: !incapacitated, value: '+Wis',
      about: 'You add your Wisdom modifier to the damage you deal with any Druid cantrip.' },
  ].filter(Boolean);
};

export default function DruidPassiveTiles({ form, characterId, combatState }) {
  const tiles = druidPassiveTiles(form, characterId, combatState);
  if (!tiles.length) return null;
  return <>{tiles.map(({ id, name, Icon, accent, lit, value, about }) => (
    <button key={id} type="button" data-action-tile={`druid:${id}`} aria-label={`${name}: ${lit ? 'active' : 'inactive'}`} title={about}
      className={`combat-hud-resource-tile combat-hud-feature-action-tile combat-hud-resource-tile--passive${lit ? '' : ' combat-hud-resource-tile--dormant'}`}
      style={{ '--hud-resource-accent': accent }} onClick={() => notify(`${name}: ${about}`, 'info')}>
      <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Icon size={20}/></span>
      <span className="combat-hud-resource-tile__name">{name}</span>
      <span className="combat-hud-resource-tile__value">{value}</span>
    </button>
  ))}</>;
}
